import React, { useEffect, useState } from "react";
import {
  FaShoppingCart,
  FaUndo,
  FaBoxes,
  FaCheckCircle,
  FaClock,
  FaMoneyBillWave,
  FaChartBar,
  FaExclamationTriangle,
  FaUserTie,
  FaTruck,
  FaClipboardList,
  FaWarehouse,
  FaTrash,
  FaFileInvoice,
  FaBriefcase,
} from "react-icons/fa";
import ExpiryAlertModal from "../../components/modals/ExpiryAlertModal";
import { toast } from "react-toastify";
import { useAuth } from "../../context/Auth";
import { useGlobalContext } from "../../context/stateContext";
import { Navigate, useNavigate } from "react-router-dom";

const BranchManagerDashboard = () => {
  const navigate = useNavigate();
  const { activeSession, currentUser } = useGlobalContext();
  const { requestWithAuth } = useAuth();
  const [gettingDashboardData, setGettingDashboardData] = useState(false);
  const [gettingRecentActivity, setGettingRecentActivity] = useState(false);
  const [recentRequisitions, setRecentRequisitions] = useState([]);
  const [recentReturns, setRecentReturns] = useState([]);
  const [recentCreditEntries, setRecentCreditEntries] = useState([]);
  const [showExpiryAlert, setShowExpiryAlert] = useState(false);
  const [dashboardData, setDashboardData] = useState({
    branch_sales_today: "0.00",
    branch_sales_month: "0.00",
    sales_count_today: 0,
    pending_returns_approval: 0,
    returns_value: 0,
    approved_returns_month: 0,
    pending_requisitions: 0,
    approved_requisitions: 0,
    pending_warehouse_supplies: 0,
    supply_value: 0,
    credit_entries_month: 0,
    credit_value_month: 0,
    returns_to_warehouse_pending: 0,
    returns_to_warehouse_value: 0,
    low_stock_items: 0,
    expiring_drugs: 0,
    expired_drugs: 0,
    total_drugs: 0,
    total_staff: 0,
  });

  // Check if alert should be shown
  useEffect(() => {
    const checkExpiryAlert = () => {
      const today = new Date().toISOString().split("T")[0];
      const dismissedDate = localStorage.getItem("expiryAlertDismissed");
      const hasExpiryIssues =
        dashboardData?.expiring_drugs > 0 || dashboardData?.expired_drugs > 0;
      const shouldShow = hasExpiryIssues && dismissedDate !== today;

      if (shouldShow) {
        setTimeout(() => {
          setShowExpiryAlert(true);
        }, 500);
      }
    };

    if (!gettingDashboardData) {
      checkExpiryAlert();
    }
  }, [
    gettingDashboardData,
    dashboardData?.expiring_drugs,
    dashboardData?.expired_drugs,
  ]);

  const handleCloseExpiryAlert = () => {
    setShowExpiryAlert(false);
  };

  const handleCloseAndHideExpiryAlert = () => {
    if (dashboardData?.expired_drugs > 0) {
      toast.error(
        `You have ${dashboardData?.expired_drugs} expired drugs, please clear them before continuing`
      );
      return;
    }
    const today = new Date().toISOString().split("T")[0];
    localStorage.setItem("expiryAlertDismissed", today);
    setShowExpiryAlert(false);
  };

  const handleViewExpiringDrugs = () => {
    const branch_id = activeSession?.branch?.id;
    setShowExpiryAlert(false);
    if (dashboardData?.expired_drugs === 0) {
      const today = new Date().toISOString().split("T")[0];
      localStorage.setItem("expiryAlertDismissed", today);
    }
    navigate(`/inventory/branch/expiring-drugs/${branch_id}`);
  };

  const getBranchManagerDashboardData = async () => {
    setGettingDashboardData(true);
    try {
      const response = await requestWithAuth(
        `/users/branch-manager/dashboard`,
        "GET"
      );
      setDashboardData(response?.data);
    } catch (error) {
      toast.error("Failed to get dashboard data");
    } finally {
      setGettingDashboardData(false);
    }
  };

  const getRecentActivity = async () => {
    setGettingRecentActivity(true);
    try {
      const [requisitionsRes, returnsRes] = await Promise.all([
        requestWithAuth(`/users/dispenser/recent-requisitions/`, "GET"),
        requestWithAuth(`/users/branch-manager/recent-returns`, "GET"),
      ]);
      setRecentRequisitions(requisitionsRes?.data || []);
      setRecentReturns(returnsRes?.data || []);
    } catch (error) {
      toast.error("Failed to get recent activity data");
    } finally {
      setGettingRecentActivity(false);
    }
  };

  useEffect(() => {
    getBranchManagerDashboardData();
    getRecentActivity();
  }, []);

  const StatCard = ({
    title,
    value,
    subtitle,
    additionalInfo,
    icon: Icon,
    color,
    linkTo,
  }) => (
    <div
      className={`rounded-lg p-6 ${color} text-white shadow-lg hover:shadow-xl transition-shadow`}
    >
      <div className="flex items-center justify-between mb-4">
        <Icon className="text-2xl opacity-80" />
        <div className="text-right">
          <h3 className="text-2xl font-bold">{value}</h3>
          <p className="text-sm opacity-90">{title}</p>
        </div>
      </div>
      {subtitle && <p className="text-xs opacity-75 mb-2">{subtitle}</p>}
      {additionalInfo && (
        <p className="text-xs opacity-75 mb-3">{additionalInfo}</p>
      )}
      {linkTo && (
        <button
          onClick={() => navigate(linkTo)}
          className="inline-flex items-center gap-1 text-sm hover:underline cursor-pointer"
        >
          Access <FaClipboardList className="text-[10px]" />
        </button>
      )}
    </div>
  );

  const SimpleCard = ({
    title,
    value,
    linkTo,
    color = "bg-gray-100",
    subtitle,
    icon: Icon,
  }) => (
    <div
      className={`rounded-lg p-6 ${color} text-center shadow-md hover:shadow-lg transition-shadow`}
    >
      <div className="flex justify-center mb-3">
        {Icon && <Icon className="text-3xl text-gray-600" />}
      </div>
      <h3 className="text-2xl font-bold mb-2 text-gray-800">{value}</h3>
      <p className="text-sm text-gray-600 mb-3">{title}</p>
      {subtitle && <p className="text-xs text-gray-500 mb-3">{subtitle}</p>}
      {linkTo && (
        <button
          onClick={() => navigate(linkTo)}
          className="inline-flex items-center gap-1 text-sm text-blue-600 hover:underline cursor-pointer"
        >
          View <FaClipboardList className="text-[12px]" />
        </button>
      )}
    </div>
  );

  if (gettingDashboardData) {
    return (
      <div className="p-6 max-w-7xl mx-auto">
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-gray-900 mb-2 flex items-center gap-2">
            <FaBriefcase className="text-blue-600" />
            Branch Manager Dashboard
          </h1>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <div
              key={i}
              className="bg-gray-200 rounded-lg h-36 animate-pulse"
            ></div>
          ))}
        </div>
      </div>
    );
  }


  if (currentUser?.branch != activeSession?.branch?.name) {
    return <Navigate to={"/dispenser/dashboard"} />
  }

  return (
    <div className="p-6 max-w-7xl mx-auto min-h-screen">
      <ExpiryAlertModal
        isOpen={showExpiryAlert}
        onClose={handleCloseExpiryAlert}
        onHideModal={handleCloseAndHideExpiryAlert}
        expiringDrugs={dashboardData.expiring_drugs}
        expiredDrugs={dashboardData.expired_drugs}
        onViewDetails={handleViewExpiringDrugs}
      />
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2 flex items-center gap-3">
          <FaBriefcase className="text-blue-600" />
          Branch Manager Dashboard
        </h1>
        <p className="text-gray-600">
          Branch oversight, approvals, and inventory management
        </p>
      </div>

      {/* Approval & Authorization Functions */}
      <div className="mb-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-2 flex items-center gap-2">
          <FaCheckCircle className="text-gray-600" />
          Approvals & Authorization
        </h2>
        <p className="text-sm text-gray-600 mb-4">
          Pending items requiring your approval
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
        <StatCard
          title="Returns Approval"
          value={dashboardData?.pending_returns_approval}
          subtitle="Returns pending approval"
          icon={FaUndo}
          color="bg-orange-600"
          linkTo="/pos/return"
        />

        <StatCard
          title="Authorize Warehouse Supply"
          value={dashboardData?.pending_warehouse_supplies}
          additionalInfo="Approve incoming supplies"
          icon={FaCheckCircle}
          color="bg-green-600"
          linkTo="/inventory/requisition"
        />

        <StatCard
          title="Requisitions Pending"
          value={dashboardData?.pending_requisitions}
          additionalInfo="Review warehouse requisitions"
          icon={FaBoxes}
          color="bg-purple-600"
          linkTo="/inventory/requisition"
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <SimpleCard
          title="Total Drugs in Branch"
          value={`${dashboardData?.total_drugs}`}
          subtitle="All items at this location"
          linkTo={`/inventory/branch/${activeSession?.branch?.id}/`}
          color="bg-white border-2 border-blue-200 text-blue-800"
          icon={FaChartBar}
        />

        <SimpleCard
          title="Low Stock Alert"
          value={dashboardData?.low_stock_items}
          subtitle="Items requiring restock"
          linkTo="/inventory/reorder/level"
          color="bg-white border-2 border-red-200 text-red-800"
          icon={FaExclamationTriangle}
        />

        <SimpleCard
          title="Expiry Alert"
          value={dashboardData?.expiring_drugs}
          subtitle="Items about to expire"
          linkTo={`/inventory/branch/expiring-drugs/${activeSession?.branch?.id}`}
          color="bg-white border-2 border-orange-200 text-orange-800"
          icon={FaClock}
        />

        <SimpleCard
          title="Branch Staff"
          value={dashboardData?.total_staff}
          subtitle="Active team members"
          // linkTo="/staff/branch"
          color="bg-white border-2 border-green-200 text-green-800"
          icon={FaUserTie}
        />
      </div>

      {/* Recent Activity */}
      <div className="mb-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-2">
          Recent Activity
        </h2>
        <p className="text-sm text-gray-600 mb-4">
          Latest transactions and requests
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        {/* Recent Requisitions */}
        <div className="bg-white rounded-lg p-6 shadow-md">
          <div className="flex items-center gap-3 mb-4">
            <FaBoxes className="text-purple-600 text-xl" />
            <h3 className="text-lg font-semibold text-gray-900">
              Recent Requisitions
            </h3>
          </div>
          <div className="space-y-3">
            {recentRequisitions.length > 0 ? (
              recentRequisitions.map((requisition, index) => (
                <div
                  key={index}
                  className="flex justify-between items-center pb-3 border-b"
                >
                  <div>
                    <p className="text-sm font-medium text-gray-900">
                      {requisition.drug}
                    </p>
                    <p className="text-xs text-gray-500">{requisition.time}</p>
                  </div>
                  <span className="px-2 py-1 text-xs rounded-full bg-purple-100 text-purple-700">
                    {requisition.status}
                  </span>
                </div>
              ))
            ) : (
              <p className="text-sm text-gray-500">No recent requisitions</p>
            )}
          </div>
          <button
            onClick={() => navigate("/inventory/requisition")}
            className="mt-4 text-sm text-blue-600 hover:underline cursor-pointer"
          >
            View all requisitions →
          </button>
        </div>

        {/* Recent Returns */}
        <div className="bg-white rounded-lg p-6 shadow-md">
          <div className="flex items-center gap-3 mb-4">
            <FaUndo className="text-orange-600 text-xl" />
            <h3 className="text-lg font-semibold text-gray-900">
              Recent Returns
            </h3>
          </div>
          <div className="space-y-3">
            {recentReturns.length > 0 ? (
              recentReturns.map((returnItem, index) => (
                <div
                  key={index}
                  className="flex justify-between items-center pb-3 border-b"
                >
                  <div>
                    <p className="text-sm font-medium text-gray-900">
                      {returnItem.drug}
                    </p>
                    <p className="text-xs text-gray-500">{returnItem.time}</p>
                  </div>
                  <span className="px-2 py-1 text-xs rounded-full bg-orange-100 text-orange-700">
                    {returnItem.status}
                  </span>
                </div>
              ))
            ) : (
              <p className="text-sm text-gray-500">No recent returns</p>
            )}
          </div>
          {/* <button
            onClick={() => navigate("/sales/returns/approve")}
            className="mt-4 text-sm text-blue-600 hover:underline cursor-pointer"
          >
            View all returns →
          </button> */}
        </div>
      </div>

      {/* Quick Actions */}
      <div className="bg-white rounded-lg p-6 shadow-md">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          Quick Actions
        </h3>
        <div className="flex flex-wrap gap-3">
          <button
            onClick={() => navigate("/inventory/requisition")}
            className="bg-green-600 text-white px-4 py-2 rounded-lg text-sm hover:bg-green-700 transition-colors cursor-pointer flex items-center gap-2"
          >
            <FaCheckCircle /> Authorize Supply
          </button>
          <button
            onClick={() => navigate("/pos/return")}
            className="bg-orange-600 text-white px-4 py-2 rounded-lg text-sm hover:bg-orange-700 transition-colors cursor-pointer flex items-center gap-2"
          >
            <FaUndo /> Approve Returns
          </button>
        </div>
      </div>
    </div>
  );
};

export default BranchManagerDashboard;
